import { useEffect, useRef } from "react";
import "../App.css";
import { motion } from "framer-motion";
import { useState } from "react";
import HighlightOffSharpIcon from "@mui/icons-material/HighlightOffSharp";
import { Coordinate } from "ol/coordinate";
import { weatherMapECMF } from "../mainLayer/weather/weatherLayer";
import proj4 from "proj4";
import { ecmf3hEnd, ecmf3hInterval, ecmf3hStart, ecmf6hEnd, ecmf6hInterval, ecmf6hStart, layerStartDate } from "../TimeLine/BackgroundMapTimeLine/TimeLineService";

interface Props {
  updateParentState: (newValue: boolean) => void;
  locationCoordinates?: Coordinate;
}

const LocationSideBar: React.FC<Props> = ({ updateParentState, locationCoordinates }) => {
  const [open, setOpen] = useState<boolean>(false);
  const resultMapECMWF = new Map<number, string>();
  const windForcast = new Map<string, string>();
  const tempForcast = new Map<string, string>();

  const handleToggle = () => {
    updateParentState(false);
    setOpen(false);
  };

  function formatTimestamp(timestamp: number): string {
    const date = new Date(timestamp * 1000);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hour = String(date.getHours()).padStart(2, "0");
    const minute = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}-${hour}:${minute}`;
  }

  function parseDateString(dateString: string): string | null {
    const offsetMinutes = new Date().getTimezoneOffset();
    const dateRegex = /^(\d{4})-(\d{2})-(\d{2})-(\d{2}):(\d{2})$/;
    const match = dateString.match(dateRegex);
    if (!match) {
      console.error("Invalid date format:", dateString);
      return null;
    }
    const [, year, month, day, hour, minute] = match;
    const date = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute)
    );
    date.setMinutes(date.getMinutes() + offsetMinutes);
    const formattedDate = `${date.getFullYear()}-${String(
      date.getMonth() + 1
    ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}-${String(
      date.getHours()
    ).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
    return formattedDate;
  }

 useEffect(() => {
  (async () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let i = 0;
    const tempMap = new Map();
    for (; i <= 48; i++) {
      const value = ecmf3hStart + i * ecmf3hInterval;
      if (value > ecmf3hEnd) break;
      const dateStr = formatTimestamp(value);
      const entryDate = new Date(dateStr.replace(/-(\d{2}):(\d{2})$/, 'T$1:$2'));
      if (entryDate >= today) {
        tempMap.set(i, dateStr);
      }
    }

    for (let j = i; j <= 64; j++) {
      const value = ecmf6hStart + (j - 48) * ecmf6hInterval;
      if (value > ecmf6hEnd) break;
      const dateStr = formatTimestamp(value);
      const entryDate = new Date(dateStr.replace(/-(\d{2}):(\d{2})$/, 'T$1:$2'));
      if (entryDate >= today) {
        tempMap.set(j, dateStr);
      }
    }

    resultMapECMWF.clear();
    tempMap.forEach((value, key) => {
      resultMapECMWF.set(key, value);
    });

    // Process each date and fetch data
    for (let i = 0; i <= resultMapECMWF.size; i++) {
      const dateBefore = resultMapECMWF.get(i) ?? "";
      const dayPass = parseDateString(dateBefore);
      if (dayPass) {
        const QUERY_LAYERS = `weather:${layerStartDate}_ecmwf_${dayPass}:00_dsuvspeed_1000hpa`;
        const QUERY_LAYERS_TEMP = `weather:${layerStartDate}_ecmwf_${dayPass}:00_temp`;

        const longitude = locationCoordinates?.[0] ?? 0;
        const latitude = locationCoordinates?.[1] ?? 0;
        const convertedCoordinates = proj4("EPSG:3857", "EPSG:4326", [
          longitude,
          latitude,
        ]);
        const delta = 0.001;
        const minX = convertedCoordinates[0] - delta;
        const maxX = convertedCoordinates[0] + delta;
        const minY = convertedCoordinates[1] - delta;
        const maxY = convertedCoordinates[1] + delta;

        const url =
          "https://wms.tecxal.com/visura/weatherwmseagle?SERVICE=WMS&VERSION=1.1.1&REQUEST=GetFeatureInfo&FORMAT=image%2Fjpeg&TRANSPARENT=true&QUERY_LAYERS=" +
          encodeURIComponent(QUERY_LAYERS) +
          "&STYLES&LAYERS=" +
          encodeURIComponent(QUERY_LAYERS) +
          "&exceptions=application%2Fvnd.ogc.se_inimage&INFO_FORMAT=application/json&FEATURE_COUNT=50&X=50&Y=50&SRS=EPSG%3A4326&WIDTH=101&HEIGHT=101&BBOX=" +
          encodeURIComponent(minX + "," + minY + "," + maxX + "," + maxY);

        const urlTemp =
          "https://wms.tecxal.com/visura/weatherwmseagle?SERVICE=WMS&VERSION=1.1.1&REQUEST=GetFeatureInfo&FORMAT=image%2Fjpeg&TRANSPARENT=true&QUERY_LAYERS=" +
          encodeURIComponent(QUERY_LAYERS_TEMP) +
          "&STYLES&LAYERS=" +
          encodeURIComponent(QUERY_LAYERS_TEMP) +
          "&exceptions=application%2Fvnd.ogc.se_inimage&INFO_FORMAT=application/json&FEATURE_COUNT=50&X=50&Y=50&SRS=EPSG%3A4326&WIDTH=101&HEIGHT=101&BBOX=" +
          encodeURIComponent(minX + "," + minY + "," + maxX + "," + maxY);

        try {
          const response = await fetch(url);
          const data = await response.json();
          if (data.features?.length > 0) {
            const featureInfo = data.features[0].properties;
            const windSpeed = featureInfo.GRAY_INDEX;
            const roundedWindSpeed = Math.round(windSpeed);
            const windSpeedWithUnit = `${roundedWindSpeed} m/s`;
            windForcast.set(dateBefore, windSpeedWithUnit);
            console.log("Wind speed data:", windSpeedWithUnit);
          } else {
            console.log("No feature data found");
          }

        } catch (error) {
          console.error("Error fetching GetFeatureInfo:", error);
        }
      }
    }
     console.log("================", windForcast);
  })();
}, []);



  const sideContainerVariants = {
    true: {
      width: "50rem",
    },
    false: {
      transition: {
        delay: 0.6,
      },
    },
  };

  const sidebarVariants = {
    true: {},
    false: {
      width: "20rem",
      transition: {
        delay: 0.4,
      },
    },
  };

  const [query, setQuery] = useState("");

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = () => {

    setQuery("");
  };


  return (
    <div className="App">
      <motion.div
        data-Open={open}
        variants={sideContainerVariants}
        initial={`${open}`}
        animate={`${open}`}
        className="locationsidebar_container"
      >
        {/* sidebar div */}

        <motion.div
          className="locationsidebar"
          initial={`${open}`}
          animate={`${open}`}
          variants={sidebarVariants}
        >
          {/* lines_icon */}
          <motion.div
            whileHover={{
              backgroundColor: "rgba(255, 255, 255, 0.3)",

              border: "1px solid rgba( 255, 255, 255, 0.18 )",
            }}
            onClick={handleToggle}
            onTouchStart={handleToggle}
            className="lines_icon"
          >
            <HighlightOffSharpIcon style={{ color: "red", fontSize: 40 }} />
          </motion.div>

          <div className="locationgroups">
            <div className="locationgroup">
              srilanka

            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default LocationSideBar;
